// Cart Drawer Navigation Management
class CartDrawerNavigation {
  constructor() {
    this.currentTab = 'cart'; // Default to cart
    this.wishlistData = [];
    this.init();
  }

  init() {
    this.setupTabNavigation();
    this.setupWishlistEventListeners();
    this.setupCartEventListeners();
  }

  setupTabNavigation() {
    const navItems = document.querySelectorAll('.sq-cart-drawer__navigation-item');
    
    navItems.forEach(item => {
      item.addEventListener('click', (e) => {
        const tabName = item.getAttribute('data-tab');
        this.switchTab(tabName);
      });
    });
  }

  switchTab(tabName) {
    if (this.currentTab === tabName) return;

    // Update navigation active state
    const navItems = document.querySelectorAll('.sq-cart-drawer__navigation-item');
    navItems.forEach(item => {
      item.classList.remove('sq-cart-drawer__navigation-item--active');
      if (item.getAttribute('data-tab') === tabName) {
        item.classList.add('sq-cart-drawer__navigation-item--active');
      }
    });

    // Update content visibility
    const tabContents = document.querySelectorAll('.tab-content');
    tabContents.forEach(content => {
      content.classList.remove('tab-content--active');
    });

    const activeContent = document.getElementById(`${tabName}-tab-content`);
    if (activeContent) {
      activeContent.classList.add('tab-content--active');
    }

    this.currentTab = tabName;

    // Handle footer visibility
    const cartDrawerFooter = document.querySelector('.m-cart-drawer__footer');
    if (cartDrawerFooter) {
      cartDrawerFooter.style.display = tabName === 'favorites' ? 'none' : 'block';
    }
  }

  setupWishlistEventListeners() {
    // Listen for wishlist header ready event
    document.addEventListener('wishlist:header:ready', (event) => {
      const wishlistData = event.detail.localWishlistIds;
      this.onWishlistDataReady(wishlistData);
    });

    // Listen for wishlist header updates
    document.addEventListener('wishlist:header:updated', (event) => {
      const { action, productId, variantId, localWishlistIds } = event.detail;
      this.onWishlistDataUpdated(action, productId, variantId, localWishlistIds);
    });

    // If wishlist header is already ready, get the data immediately
    if (window.WishlistHeader && window.WishlistHeader.localWishlistIds) {
      this.onWishlistDataReady(window.WishlistHeader.localWishlistIds);
    }
  }

  setupCartEventListeners() {
    // Listen for cart update events from MinimogEvents (theme's event system)
    if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
      window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, (event) => {
        this.switchTab('cart');
        let length = 0;
      for (const [key, value] of Object.entries(this.wishlistData)) {
        for (const variant of value.variants) {
          length++;
        }
      }
      this.updateWishlistCount(length);
      this.renderWishlistItems(this.wishlistData);
      });
    }

    // Listen for product-ajax:added custom event (dispatched on document)
    document.addEventListener('product-ajax:added', (event) => {
      this.switchTab('cart');
      let length = 0;
      for (const [key, value] of Object.entries(this.wishlistData)) {
        for (const variant of value.variants) {
          length++;
        }
      }
      this.updateWishlistCount(length);
      this.renderWishlistItems(this.wishlistData);
    });

    // Listen for cart:updated custom event as a fallback
    document.addEventListener('cart:updated', (event) => {
      this.switchTab('cart');
      let length = 0;
      for (const [key, value] of Object.entries(this.wishlistData)) {
        for (const variant of value.variants) {
          length++;
        }
      }
      this.updateWishlistCount(length);
      this.renderWishlistItems(this.wishlistData);
    });
  }

  onWishlistDataReady(wishlistData) {
    // Handle initial wishlist data
    this.wishlistData = wishlistData;
    let length = 0;
    for (const [key, value] of Object.entries(wishlistData)) {
      for (const variant of value.variants) {
        length++;
      }
    }
    this.updateWishlistCount(length);
    this.renderWishlistItems(wishlistData);
  }

  onWishlistDataUpdated(action, productId, variantId, wishlistData) {
    // Handle wishlist data updates
    this.wishlistData = wishlistData;
    let length = 0;
    for (const [key, value] of Object.entries(wishlistData)) {
      for (const variant of value.variants) {
        length++;
      }
    }
    this.updateWishlistCount(length);
    this.renderWishlistItems(wishlistData);
  }

  updateWishlistCount(wishlistCount) {
    const wishlistCountElement = document.querySelector('sup#wishlist-count');
    if (wishlistCountElement) {
      wishlistCountElement.textContent = wishlistCount;
    }
  }

  async getProductData(productId) {
    try {
      // Use the Storefront API to fetch product data
      const product = await window.MinimogTheme.StorefrontAPI.helpers.fetchProductForCart(productId);
      return product;
    } catch (error) {
      console.error(`❌ Error fetching product data for ID ${productId}:`, error);
      return null;
    }
  }

  async renderWishlistItems(wishlistData) {
    const wishlistContainer = document.getElementById('wishlist-items-container');
    
    if (!wishlistContainer) {
      console.warn("⚠️ Wishlist container not found");
      return;
    }

    // Clear existing items
    wishlistContainer.innerHTML = '';


    // Show loading state
    wishlistContainer.innerHTML = '<div class="sq-wishlist-loading">Loading wishlist items...</div>';

    try {
      // Fetch product data for all wishlist items
      const productPromises = [];
      
      for (const [key, value] of Object.entries(wishlistData)) {
        
        // Extract product ID from the key or value structure
        let productId = null;
        
        if (typeof value === 'object' && value.productId) {
          productId = value.productId;
        } else if (typeof value === 'string' || typeof value === 'number') {
          productId = value;
        } else if (typeof key === 'string' || typeof key === 'number') {
          productId = key;
        }

        if (productId) {
          productPromises.push(this.getProductData(productId));
        }
      }

      // Wait for all product data to be fetched
      const products = await Promise.all(productPromises);
      
      
      // Clear loading state
      wishlistContainer.innerHTML = '';
      // Render each product
      products.forEach((product) => {
        if (product) {
          const wishlistItem = this.createWishlistItemHTML(product);
          wishlistContainer.appendChild(wishlistItem);
        }
      }); 

      // If no products were found
      if (products.filter(p => p !== null).length === 0) {
        wishlistContainer.innerHTML = '<div class="sq-wishlist-empty">Your wishlist is empty</div>';
      }

    } catch (error) {
      console.error("❌ Error rendering wishlist items:", error);
      wishlistContainer.innerHTML = '<div class="sq-wishlist-error">Error loading wishlist items</div>';
    }
  }

  isVariantInWishlist(variantId) {
    for (const [key, value] of Object.entries(this.wishlistData)) {
      if (value.variants && value.variants.includes(variantId.toString())) {
        return true;
      }
    }
    return false;
  }

  createWishlistItemHTML(product) {
    const wishlistItem = document.createElement('a');
    wishlistItem.href = `/products/${product.handle}`;
    wishlistItem.classList.add('sq-wishlist-item');
    // Get the featured image or first available image
    const image = product.featured_image || (product.images && product.images[0]);
    const imageUrl = image ? image.url : '';
    const imageAlt = image ? image.alt : product.title;
    // Find the variant that's actually in the wishlist
    const variant = product.variants.find(v => this.isVariantInWishlist(v.id)) || product.variants[0];
    // Get the price (convert from cents to display format)
    const price = Number(variant.price) / 100;
    wishlistItem.innerHTML = `
      <div class="sq-wishlist-item__wrapper">
      <div class="sq-wishlist-item__image">
        <div class="wishlist-engine" data-product_id="${product.id}" data-variant_id="${variant.id}" data-full_button="false" data-css="true"></div>
        ${imageUrl ? `<img src="${imageUrl}" alt="${imageAlt}">` : '<div class="sq-wishlist-item__no-image">No image</div>'}
      </div>
      <div class="sq-wishlist-item__details">
        <div class="sq-wishlist-item__title">
          ${product.title.split(' - ').length === 2 ? product.title.split(' - ')[1] : product.title.split(' - ')[0]}
        </div>
        
        <div style="display: flex; align-items: center; gap: 6px;">
          ${price ? `<div class="sq-wishlist-item__price">₩ ${price}</div>` : ''}
        </div>
        
      </div>
      </div>
    `;

    // Add event listener for remove button
    const removeButton = wishlistItem.querySelector('.sq-wishlist-item__remove');
    if (removeButton) {
      removeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.removeFromWishlist(product.handle);
      });
    }

    return wishlistItem;
  }

  removeFromWishlist(productHandle) {
    // Integrate with your existing wishlist system
    if (window.MinimogTheme && window.MinimogTheme.Wishlist) {
      window.MinimogTheme.Wishlist.removeFromWishlist(productHandle);
    } else {
      console.warn("⚠️ Wishlist system not found");
    }
  }

  async addToCart(button) {
    const productId = button.getAttribute('product-id');
    const variantId = button.getAttribute('variant-id');
    
    if (!variantId) {
      console.error("❌ No variant ID provided for add to cart");
      return;
    }

    // Show loading state
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    button.disabled = true;

    try {
      // Prepare request data
      let requestData = {
        'items': [{
          'id': parseInt(variantId),
          'quantity': 1
        }]
      };

      // Get cart sections to update if cart drawer exists
      const cart = document.querySelector('m-cart-drawer');
      if (cart) {
        requestData.sections = cart.getSectionsToRender().map(section => section.id);
        requestData.sections_url = window.location.pathname;
      }

      // Make the add to cart request
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestData)
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
      const responseData = await response.json();

      if (responseData.status) {
        // Handle error
        console.error("❌ Add to cart error:", responseData.message);
        
        // Show error notification
        if (window.MinimogTheme && window.MinimogTheme.Notification) {
          window.MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: responseData.message || 'Error adding item to cart',
            last: 3000,
            sticky: true
          });
        }
        
        // Emit error event
        if (window.MinimogEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartError, {
            source: 'wishlist-add-to-cart',
            productVariantId: variantId,
            errors: responseData.description,
            message: responseData.message
          });
        }
      } else {
        // Success - handle cart update

        if (cart && window.MinimogSettings.enable_cart_drawer) {
          // Update cart drawer content
          cart.renderContents(responseData);
          
          // Show success notification in cart drawer
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: cart.querySelector('m-cart-drawer-items'),
              method: 'prepend',
              type: 'success',
              message: window.MinimogStrings.itemAdded || 'Item added to cart',
              delay: 400
            });
          }

          // Open cart drawer
          if (window.MinimogEvents) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
          }
        } else {
          // Show success notification
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: document.body,
              method: 'appendChild',
              type: 'success',
              message: window.MinimogStrings.itemAdded || 'Item added to cart',
              last: 3000,
              sticky: true
            });
          }

          // Update cart count bubble
          const response = await fetch(window.Shopify.routes.root + 'cart.json');
          const cartData = await response.json();
          
          if (cartData) {
            const count = cartData.item_count;
            const cartCounts = document.querySelectorAll('.m-cart-count-bubble');
            cartCounts.forEach(cartCount => {
              if (count > 0) {
                cartCount.textContent = count;
                cartCount.classList.remove('m:hidden');
              } else {
                cartCount.classList.add('m:hidden');
              }
            });
          }
        }

        // Emit cart update event
        if (window.MinimogEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, responseData);
        }

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('product-ajax:added', {
          detail: {
            product: responseData
          }
        }));
      }

    } catch (error) {
      console.error("❌ Add to cart request failed:", error);
      
      // Show error notification
      if (window.MinimogTheme && window.MinimogTheme.Notification) {
        window.MinimogTheme.Notification.show({
          target: document.body,
          method: 'appendChild',
          type: 'error',
          message: 'Failed to add item to cart. Please try again.',
          last: 3000,
          sticky: true
        });
      }
    } finally {
      // Reset button state
      button.textContent = originalText;
      button.disabled = false;
      // Note: switchTab('cart') is now handled by setupCartEventListeners
      let length = 0;
      for (const [key, value] of Object.entries(this.wishlistData)) {
        for (const variant of value.variants) {
          length++;
        }
      }
      this.updateWishlistCount(length);
      this.renderWishlistItems(this.wishlistData);
      
    }
  }
}

// Initialize cart drawer navigation when DOM is ready
let cartDrawerNav;

function initializeCartDrawerNavigation() {
  if (!cartDrawerNav) {
    cartDrawerNav = new CartDrawerNavigation();
    window.cartDrawerNav = cartDrawerNav; // Make it globally accessible
  }
}

// Auto-initialize if DOM is already ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartDrawerNavigation);
} else {
  initializeCartDrawerNavigation();
}

// Export for manual initialization if needed
window.CartDrawerNavigation = CartDrawerNavigation;
window.initializeCartDrawerNavigation = initializeCartDrawerNavigation; 
